/**
 * Magniberg: images fade in as they scroll into view, like the current site.
 *
 * An inline script in snippets/mb-theme-styles.liquid adds `mb-image-fade` to <html>
 * before the page paints; with that class, assets/mb-theme.css keeps the page's images
 * transparent until they carry `mb-image-fade--in`. This module adds that class to each
 * image once it is on screen and has loaded, which starts the 0.5s fade.
 *
 * If this module never runs, the inline script removes the <html> class again after a
 * few seconds, so images cannot stay hidden.
 */
const IMAGES = 'main img';
// A product card's second image is swapped in on hover: it must be ready, not fading.
const EXCLUDED = 'product-card slideshow-slide:not(:first-of-type) img, [data-mb-no-fade] img';
const SHOWN = 'mb-image-fade--in';

if (document.documentElement.classList.contains('mb-image-fade')) {
  /** Images that have been shown; morphs can strip the class, so it is put back. */
  const shown = new WeakSet();

  /** @param {HTMLImageElement} image */
  const show = (image) => {
    shown.add(image);
    image.classList.add(SHOWN);
  };

  const visibility = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;

        const image = /** @type {HTMLImageElement} */ (entry.target);
        visibility.unobserve(image);

        if (image.complete) {
          show(image);
        } else {
          image.addEventListener('load', () => show(image), { once: true });
          image.addEventListener('error', () => show(image), { once: true });
        }
      }
    },
    { rootMargin: '0px 0px -5% 0px' }
  );

  /** @param {ParentNode} root */
  const observe = (root) => {
    for (const image of root.querySelectorAll(IMAGES)) {
      if (shown.has(image) || image.matches(EXCLUDED)) continue;
      visibility.observe(image);
    }
  };

  observe(document);

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        // A re-render reset the class on an image that was already shown.
        const image = mutation.target;
        if (image instanceof HTMLImageElement && shown.has(image) && !image.classList.contains(SHOWN)) {
          image.classList.add(SHOWN);
        }
        continue;
      }

      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches(IMAGES) && !node.matches(EXCLUDED)) visibility.observe(node);
        else observe(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

  /** @type {any} */ (window).mbImageFadeReady = true;
}
